import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { Heart, Users, Calendar, Download, ArrowRight, Shield } from "lucide-react";

const basePremiums: Record<string, Record<string, number>> = {
  "5": { "18-30": 320, "31-40": 450, "41-50": 780, "51-60": 1450, "60+": 2600 },
  "10": { "18-30": 580, "31-40": 820, "41-50": 1400, "51-60": 2600, "60+": 4700 },
  "15": { "18-30": 840, "31-40": 1180, "41-50": 2000, "51-60": 3700, "60+": 6700 },
  "25": { "18-30": 1350, "31-40": 1900, "41-50": 3200, "51-60": 5900, "60+": 10800 },
  "50": { "18-30": 2500, "31-40": 3500, "41-50": 5800, "51-60": 10500, "60+": 19500 },
};

export default function HealthCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/health`;

  const schemas = [
    webpageSchema(
      "Health Insurance Premium Calculator | Family Floater & Individual | ExpertAdvise.in",
      "Estimate health insurance premiums based on age, coverage amount, family size, and medical history. Compare plans from top insurers instantly.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "Health Insurance Calculator", url: pageUrl },
    ]),
  ];

  const [age, setAge] = useState("31-40");
  const [coverage, setCoverage] = useState("10");
  const [members, setMembers] = useState(2);
  const [hasCondition, setHasCondition] = useState(false);

  const premium = useMemo(() => {
    const base = basePremiums[coverage][age] || 800;
    const memberMultiplier = members === 1 ? 1 : members === 2 ? 1.7 : members === 3 ? 2.3 : members === 4 ? 2.8 : 3.2;
    const conditionMultiplier = hasCondition ? 1.3 : 1;
    return Math.round(base * memberMultiplier * conditionMultiplier);
  }, [age, coverage, members, hasCondition]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} Lakhs`;
    return `₹${val.toLocaleString()}`;
  }, []);

  const handleDownload = () => {
    alert("PDF report generation will be enabled after backend integration.");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Health Insurance Premium Calculator | Family Floater & Individual | ExpertAdvise.in"
        description="Estimate health insurance premiums based on age, coverage amount, family size, and medical history. Compare plans from top insurers instantly."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-red-50 text-red-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Insurance Calculator
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Health Insurance Premium Calculator
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Estimate your health insurance premium based on age, coverage amount, family size, and health conditions.
              Get instant estimates from top insurers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Your Details</h3>

                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Calendar className="w-4 h-4 text-primary-500" />
                      Age Group
                    </label>
                    <select
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400"
                    >
                      <option value="18-30">18 - 30 Years</option>
                      <option value="31-40">31 - 40 Years</option>
                      <option value="41-50">41 - 50 Years</option>
                      <option value="51-60">51 - 60 Years</option>
                      <option value="60+">60+ Years</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Shield className="w-4 h-4 text-primary-500" />
                      Coverage Amount (Sum Insured)
                    </label>
                    <select
                      value={coverage}
                      onChange={(e) => setCoverage(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400"
                    >
                      <option value="5">₹5 Lakhs</option>
                      <option value="10">₹10 Lakhs</option>
                      <option value="15">₹15 Lakhs</option>
                      <option value="25">₹25 Lakhs</option>
                      <option value="50">₹50 Lakhs</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Users className="w-4 h-4 text-primary-500" />
                      Family Members Covered
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <button
                          key={num}
                          onClick={() => setMembers(num)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                            members === num
                              ? "bg-primary-600 text-white"
                              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {num === 5 ? "5+" : num}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Heart className="w-4 h-4 text-primary-500" />
                      Pre-Existing Condition?
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setHasCondition(true)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          hasCondition
                            ? "bg-primary-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setHasCondition(false)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          !hasCondition
                            ? "bg-primary-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PDF Report
              </button>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Premium Card */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <div className="text-sm text-white/80 mb-2">Estimated Annual Premium</div>
                <div className="text-4xl md:text-5xl font-bold mb-4">{formatCurrency(premium * 12)}</div>
                <div className="text-sm text-white/70">≈ {formatCurrency(premium)}/month</div>
                <div className="mt-6 pt-6 border-t border-white/20 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-white/60 mb-1">Age Group</div>
                    <div className="font-semibold">{age}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Coverage</div>
                    <div className="font-semibold">₹{coverage} Lakhs</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Family</div>
                    <div className="font-semibold">{members} {members === 1 ? "Member" : "Members"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/60 mb-1">Pre-Existing</div>
                    <div className="font-semibold">{hasCondition ? "Yes" : "No"}</div>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Recommended Plans for You</h3>
                <div className="space-y-3">
                  {[
                    { name: "Star Health Comprehensive", premium: Math.round(premium * 0.95), hospitals: 14000, rating: 4.5 },
                    { name: "ICICI Lombard Complete", premium: Math.round(premium * 1.02), hospitals: 8500, rating: 4.7 },
                    { name: "Niva Bupa ReAssure 2.0", premium: Math.round(premium * 1.08), hospitals: 10500, rating: 4.6 },
                  ].map((plan, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <div className="font-semibold text-slate-900">{plan.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{plan.hospitals.toLocaleString()}+ cashless hospitals</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">{formatCurrency(plan.premium * 12)}/yr</div>
                        <div className="text-xs text-slate-500">⭐ {plan.rating}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/health-insurance"
                  className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
                >
                  View all health insurance plans
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Get Accurate Quotes from 15+ Insurers
          </h2>
          <p className="text-slate-600 mb-6">
            This calculator gives estimates. For exact premiums and personalized recommendations, talk to our advisor.
          </p>
          <Link
            to="/book-appointment?source=Health%20Calculator%20Page"
            className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
          >
            Get Personalized Quotes
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}